export const DEFAULT_NOTIFICATION_GUIDE = [
    {
        type: "ACCOUNT_STATUS_CHANGED",
        category: "Account Health",
        severity: "CRITICAL",
        businessImpact: "Extremely High",
        needsAttention: "Yes",
        recommendedAction:
            "Review Amazon Account Health immediately and address performance metrics for Seller ID."
    },
    {
        type: "AUTH_TOKEN_EXPIRED",
        category: "Integration & API",
        severity: "CRITICAL",
        businessImpact: "High",
        needsAttention: "Yes",
        recommendedAction:
            "Re-authorize SP-API token in your integration settings for Seller ID."
    },
    {
        type: "FEED_PROCESSING_FINISHED",
        category: "Catalog Feeds",
        severity: "HIGH",
        businessImpact: "High",
        needsAttention: "Yes",
        recommendedAction:
            "Inspect feed processing error report for SKU and re-upload."
    },
    {
        type: "PRICING_HEALTH",
        category: "Pricing",
        severity: "CRITICAL",
        businessImpact: "Very High",
        needsAttention: "Yes",
        recommendedAction:
            "Review pricing and Buy Box eligibility for ASIN."
    },
    {
        type: "LISTINGS_ITEM_ISSUES_CHANGE",
        category: "Catalog Quality",
        severity: "HIGH",
        businessImpact: "Very High",
        needsAttention: "Yes",
        recommendedAction:
            "Update missing attributes via inventory loader for SKU."
    },
    {
        type: "FBA_INVENTORY_AVAILABILITY_CHANGES",
        category: "FBA Inventory",
        severity: "HIGH",
        businessImpact: "Very High",
        needsAttention: "Yes",
        recommendedAction:
            "Check inventory availability and potential stock-out for ASIN."
    },
    {
        type: "LISTINGS_ITEM_MFN_QUANTITY_CHANGE",
        category: "MFN Inventory",
        severity: "MEDIUM",
        businessImpact: "High",
        needsAttention: "Yes",
        recommendedAction:
            "Synchronize inventory for SKU."
    },
    {
        type: "BRANDED_ITEM_CONTENT_CHANGE",
        category: "Brand Content",
        severity: "MEDIUM",
        businessImpact: "Medium",
        needsAttention: "No",
        recommendedAction:
            "Review content changes for ASIN."
    }
];

export function getClassification(type, guide) {
    const rule = (guide || []).find(
        (item) => item.type === type
    );

    if (rule) {
        return {
            severity: rule.severity,
            businessImpact: rule.businessImpact,
            needsAttention: rule.needsAttention,
            recommendedAction: rule.recommendedAction
        };
    }

    return {
        severity: "MEDIUM",
        businessImpact: "Medium",
        needsAttention: "Yes",
        recommendedAction: "Review notification details."
    };
}

export function formatEventTime(value) {
    if (!value) {
        return "-";
    }

    return String(value)
        .replace("T", " ")
        .replace("Z", "")
        .replace(/(\.\d{2})\d*/, "$1");
}

export function getPayloadField(payload, names) {
    if (!payload) {
        return "";
    }

    if (
        !Array.isArray(payload) &&
        typeof payload === "object"
    ) {
        for (let i = 0; i < names.length; i++) {
            const key = Object.keys(payload).find(
                (item) =>
                    item.toLowerCase() ===
                    names[i].toLowerCase()
            );

            if (
                key &&
                payload[key] != null &&
                payload[key] !== ""
            ) {
                return String(payload[key]);
            }
        }

        return "";
    }

    if (!Array.isArray(payload)) {
        return "";
    }

    for (let i = 0; i < payload.length; i++) {
        const item = payload[i];

        const label = String(
            item.label || ""
        )
            .toLowerCase()
            .replace(/[:\s]+$/g, "");

        for (let j = 0; j < names.length; j++) {
            if (
                label ===
                names[j].toLowerCase()
            ) {
                return item.value != null &&
                    item.value !== ""
                    ? String(item.value)
                    : "";
            }
        }
    }

    return "";
}

export function payloadToObject(payload) {
    if (
        payload &&
        typeof payload === "object" &&
        !Array.isArray(payload)
    ) {
        return payload;
    }

    if (!Array.isArray(payload)) {
        return {};
    }

    const obj = {};

    payload.forEach((item) => {
        if (item.isHeader) {
            return;
        }

        if (
            item.label &&
            item.label !== ":"
        ) {
            obj[item.label] = item.value;
        }
    });

    return obj;
}

export function payloadSnippet(payload) {
    const obj = payloadToObject(payload);

    try {
        return JSON.stringify(obj);
    } catch (error) {
        return "";
    }
}

export function getPrimaryId(notification) {
    if (
        notification.asin &&
        String(notification.asin).trim() !== "" &&
        notification.asin !== "-"
    ) {
        return String(notification.asin).trim();
    }

    if (
        notification.sku &&
        String(notification.sku).trim() !== "" &&
        notification.sku !== "-"
    ) {
        return String(notification.sku).trim();
    }

    if (
        notification.sellerId &&
        String(notification.sellerId).trim() !== ""
    ) {
        return String(notification.sellerId).trim();
    }

    return notification.id ? String(notification.id) : "";
}

export function mapApiNotification(row, index) {
    const payload =
        row.notificationPayload || [];

    const asinRaw =
        (row.asin && row.asin !== "-"
            ? row.asin
            : "") ||
        getPayloadField(
            payload,
            ["ASIN", "asin", "Asin"]
        ) ||
        "";

    const sku =
        row.sku ||
        getPayloadField(
            payload,
            [
                "SKU",
                "sku",
                "SellerSku",
                "sellerSku"
            ]
        ) ||
        "";

    const sellerId =
        row.sellerID ||
        row.sellerId ||
        "";

    let identifierType = "SKU-only";

    if (asinRaw) {
        identifierType = "ASIN / SKU";
    } else if (sku) {
        identifierType = "SKU-only";
    } else if (sellerId) {
        identifierType = "Seller ID";
    }

    return {
        id:
            row.notificationID ||
            `${row.notificationType}-${row.eventTime}-${index}`,

        store:
            row.storeName || "",

        asin: asinRaw,

        sku,

        sellerId,

        identifierType,

        type:
            row.notificationType || "",

        payload,

        eventTime:
            formatEventTime(row.eventTime),

        eventTimeRaw:
            row.eventTime,

        status:
            row.status || "OPEN"
    };
}

export function severityBadgeClass(severity) {
    if (severity === "CRITICAL") {
        return "anm-badge-critical";
    }

    if (severity === "HIGH") {
        return "anm-badge-high";
    }

    if (severity === "MEDIUM") {
        return "anm-badge-medium";
    }

    return "anm-badge-low";
}

export function statusBadgeClass(status) {
    if (status === "RESOLVED") {
        return "anm-badge-status-resolved";
    }

    if (status === "NEW") {
        return "anm-badge-status-new";
    }

    if (status === "IN PROGRESS") {
        return "anm-badge-status-progress";
    }

    if (status === "INFORMATIONAL") {
        return "anm-badge-status-info";
    }

    return "anm-badge-status-open";
}

export function entityBadgeClass(entityType) {
    if (entityType === "Seller ID") {
        return "anm-badge-critical";
    }

    if (entityType === "ASIN / SKU") {
        return "anm-badge-status-new";
    }

    return "anm-badge-medium";
}

export function isWithinDateFilter(
    eventTimeRaw,
    filterDate
) {
    if (!filterDate) {
        return true;
    }

    if (!eventTimeRaw) {
        return false;
    }

    const date = new Date(eventTimeRaw);

    if (isNaN(date.getTime())) {
        return false;
    }

    const now = new Date();

    if (filterDate === "today") {
        return (
            date.toDateString() ===
            now.toDateString()
        );
    }

    if (filterDate === "7days") {
        const from = new Date(now);

        from.setDate(
            from.getDate() - 7
        );

        return date >= from;
    }

    return true;
}